import React, { useState, useEffect, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertIcon, CheckIcon, CloseIcon } from '@shared/components/icons';

// Button Component
export const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'start',
  className = '',
  ...props
}, ref) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500 shadow-soft hover:shadow-medium',
    secondary: 'bg-white text-text-primary border border-gray-200 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300 shadow-soft',
    ghost: 'text-text-secondary hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-300',
    danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 focus:ring-red-500 shadow-soft hover:shadow-medium',
    success: 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700 focus:ring-green-500 shadow-soft hover:shadow-medium',
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-5 py-2.5 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
    xl: 'px-8 py-4 text-lg gap-3',
  };
  
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {loading ? (
        <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          {Icon && iconPosition === 'start' && <Icon size={size === 'sm' ? 16 : 20} />}
          {children}
          {Icon && iconPosition === 'end' && <Icon size={size === 'sm' ? 16 : 20} />}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// Input Component
export const Input = forwardRef(({
  label,
  error,
  icon: Icon,
  className = '',
  inputClassName = '',
  labelClassName = '',
  labelStyle = {},
  ...props
}, ref) => {
  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className={`block text-sm font-medium mb-2 ${labelClassName || 'text-text-secondary'}`} style={labelStyle}>
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <span className="absolute start-4 top-1/2 -translate-y-1/2 text-text-muted">
            <Icon size={20} />
          </span>
        )}
        <input
          ref={ref}
          className={`
            w-full px-4 py-3 bg-white border rounded-xl
            text-text-primary placeholder-text-muted
            focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
            transition-all duration-200
            ${Icon ? 'ps-12' : ''}
            ${error ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' : 'border-gray-200'}
            ${inputClassName}
          `}
          {...props}
        />
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-500 flex items-center gap-1.5">
          <AlertIcon size={14} />
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Toast Component
export const Toast = ({ message, type = 'info', onClose, duration = 5000 }) => {
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const icons = {
    success: <CheckIcon className="text-white\" size={20} />,
    error: <AlertIcon className="text-white" size={20} />,
    warning: <AlertIcon className="text-white" size={20} />,
    info: <AlertIcon className="text-white" size={20} />,
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-primary-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        flex items-center gap-3 px-5 py-4 rounded-2xl shadow-strong
        ${colors[type]} text-white
        max-w-md
      `}
    >
      {icons[type]}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button 
        onClick={onClose}
        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
      >
        <CloseIcon size={18} />
      </button>
    </motion.div>
  );
};

// Toast Container
export const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <AnimatePresence>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </AnimatePresence>
  );
};

// Custom Hook for Toast
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearToasts = () => {
    setToasts([]);
  };

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  };
};

// Modal Component
export const Modal = ({ isOpen, onClose, title, children, className = '' }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={`relative bg-white rounded-3xl shadow-strong p-6 max-w-lg w-full ${className}`}
          >
            {title && (
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-text-primary">{title}</h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <CloseIcon size={20} />
                </button>
              </div>
            )}
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Loading Spinner
export const Spinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4',
  };

  return (
    <div
      className={`
        ${sizes[size]}
        border-gray-200 border-t-primary-600
        rounded-full animate-spin
        ${className}
      `}
    />
  );
};

// Loading Screen
export const LoadingScreen = ({ message }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-surface-light">
    <Spinner size="lg" />
    {message && (
      <p className="mt-4 text-text-secondary font-medium">{message}</p>
    )}
  </div>
);

// Status Badge
export const StatusBadge = ({ status, children, className = '' }) => {
  const statusStyles = {
    active: 'bg-green-500/10 text-green-600',
    inactive: 'bg-gray-500/10 text-gray-600',
    error: 'bg-red-500/10 text-red-600',
    warning: 'bg-amber-500/10 text-amber-600',
    info: 'bg-blue-500/10 text-blue-600',
  };

  const dotStyles = {
    active: 'bg-green-500 animate-pulse',
    inactive: 'bg-gray-400',
    error: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  return (
    <span className={`
      inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
      ${statusStyles[status] || statusStyles.info}
      ${className}
    `}>
      <span className={`w-2 h-2 rounded-full ${dotStyles[status] || dotStyles.info}`} />
      {children}
    </span>
  );
};

// Divider
export const Divider = ({ className = '' }) => (
  <div className={`h-px bg-gray-200 ${className}`} />
);

// Card
export const Card = ({ children, className = '', elevated = false, ...props }) => (
  <div
    className={`
      bg-white rounded-2xl
      ${elevated ? 'shadow-medium p-8' : 'shadow-soft p-6'}
      ${className}
    `}
    {...props}
  >
    {children}
  </div>
);
